// The bank statement page a bank line came from, with the line marked — the Bank Accounts register's 🔎
// "locate on statement" viewer as a shared piece, so the General Ledger can open it too (owner 2026-10-08:
// a ledger line opens its document; for a bank line that is its statement page, when the import kept
// the PDF or image). Same markup, classes and wording as the register's viewer (cashbook.js), which
// still keeps its own copy.
//   const stmt = statementViewer(companyId);
//   stmt.open({ job, date, description, reference, amount });   // a bank line and its import job
//   stmt.openPage({ job, page, title, file });                   // a page of the statement itself (0-based), no AI
//   ${() => stmt.view()}                                          // once, in a template built once
import { html, signal } from './t4.js';
import { api } from './api.js';
import { t } from './i18n.js';
import { icon } from './icons.js';

export function statementViewer(companyId){
  const P = p => '/companies/' + companyId + p;
  // a page of the job's statement; `file` names which one when the job holds two dropped together (a locate's file,
  // a review's), else the newest (3 Nov live: an OVK July line opened on the August statement)
  const pageUrl = (job, page, file) => P(`/bank-imports/${job}/page?page=${page}`) + (file ? '&file=' + encodeURIComponent(file) : '');
  const state = signal(null);   // {status:'loading'|'error'|'ready', title, job, file, imgUrl, box, page, pageCount, foundPage, foundBox, error}
  let seq = 0;                  // guards async page loads against a stale / closed viewer

  function close(){
    seq++;
    const s = state.value;
    if(s && s.imgUrl){ try { URL.revokeObjectURL(s.imgUrl); } catch { /* already revoked */ } }
    state.value = null;
  }
  const markIntoView = delay => setTimeout(() => {
    const el = document.querySelector('.cb-stmt-hl');
    if(el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
  }, delay);

  async function open(line){
    const job = line && line.job;
    if(!job) return;
    const my = ++seq;
    const title = (line.description || '').slice(0, 60) || t('cashbook.statement', 'Statement');
    state.value = { status: 'loading', title, imgUrl: '', box: null, page: 0, job };
    let data;
    try {
      data = await api.post(P(`/bank-imports/${job}/locate`),
        { date: line.date, description: line.description || '', reference: line.reference || '', amount: line.amount });
    } catch(e){
      if(my === seq) state.value = { status: 'error', title,
        error: (e.data && e.data.error) || t('cashbook.locate_failed', 'Could not reach the statement locator.') };
      return;
    }
    if(my !== seq) return;
    const errMap = {
      no_key: t('cashbook.locate_no_key', 'Add your Gemini API key in AI settings to locate lines on the statement.'),
      no_pages: t('cashbook.locate_no_source', 'The original statement image was not stored for this import (it predates this feature, or was a CSV).'),
    };
    if(data && data.error && errMap[data.error]){ state.value = { status: 'error', title, error: errMap[data.error] }; return; }
    const pageCount = (data && data.page_count) || 1;
    const foundPage = (data && typeof data.page === 'number' && data.page >= 0 && data.page < pageCount) ? data.page : 0;
    let foundBox = (data && Array.isArray(data.box)) ? data.box : null;
    // a real row is a thin strip: reject an absent / inverted box or one taller than half the page
    if(foundBox && !(foundBox.length === 4 && foundBox[0] >= 0 && foundBox[2] <= 1 && foundBox[2] > foundBox[0]
                     && (foundBox[2] - foundBox[0]) <= 0.5)) foundBox = null;
    const file = (data && data.file) || '';
    let imgUrl = '';
    try { imgUrl = await api.objectUrl(pageUrl(job, foundPage, file)); }
    catch(e){
      if(my === seq) state.value = { status: 'error', title,
        error: (e.data && e.data.error) || t('cashbook.locate_no_image', 'Could not load the statement page image.') };
      return;
    }
    if(my !== seq){ try { URL.revokeObjectURL(imgUrl); } catch { /* stale */ } return; }
    state.value = { status: 'ready', title, job, file, imgUrl, box: foundBox, page: foundPage, pageCount, foundPage, foundBox };
    markIntoView(250);
  }

  // A page of the statement itself, with no line to find (a co-op statement's review links each document to the page
  // it is printed on): page is 0-based, as the page route takes it. How many pages there are is not known up front —
  // a flip past the last page is refused, and that refusal sets it (pageCount 0 = not known yet).
  async function openPage(at){
    const job = at && at.job;
    if(!job) return;
    const my = ++seq;
    const page = Math.max(0, Math.floor(Number(at.page) || 0));
    const title = String(at.title || '').slice(0, 60) || t('cashbook.statement', 'Statement');
    const file = at.file || '';
    state.value = { status: 'loading', title, imgUrl: '', box: null, page, job, pageOnly: true };
    let imgUrl = '';
    try { imgUrl = await api.objectUrl(pageUrl(job, page, file)); }
    catch(e){
      if(my === seq) state.value = { status: 'error', title,
        error: (e.data && e.data.error) || t('cashbook.locate_no_image', 'Could not load the statement page image.') };
      return;
    }
    if(my !== seq){ try { URL.revokeObjectURL(imgUrl); } catch { /* stale */ } return; }
    state.value = { status: 'ready', title, job, file, imgUrl, box: null, page, pageCount: 0, foundPage: -1, foundBox: null,
                    pageOnly: true };
  }

  async function gotoPage(n){
    const s = state.value;
    if(!s || s.status !== 'ready' || !s.job) return;
    const last = s.pageCount ? s.pageCount - 1 : (s.pageOnly ? Infinity : 0);
    const page = Math.max(0, Math.min(last, n));
    if(page === s.page) return;
    const my = ++seq;
    let imgUrl;
    try { imgUrl = await api.objectUrl(pageUrl(s.job, page, s.file)); }
    catch {
      // leave the current page shown on a failed flip; past the end of a statement opened at a page, the current
      // page is its last
      const cur = state.value;
      if(cur && my === seq && cur.pageOnly && !cur.pageCount && page > cur.page) state.value = { ...cur, pageCount: cur.page + 1 };
      return;
    }
    const cur = state.value;
    if(!cur || my !== seq){ try { URL.revokeObjectURL(imgUrl); } catch { /* stale */ } return; }
    if(cur.imgUrl){ try { URL.revokeObjectURL(cur.imgUrl); } catch { /* already revoked */ } }
    state.value = { ...cur, imgUrl, page, box: (page === cur.foundPage ? cur.foundBox : null) };
    if(page === cur.foundPage) markIntoView(200);
  }

  function modal(s){
    let band = '';
    if(s.box){                                    // full-width band at the line's vertical position
      const pad = 1.2;
      const top = Math.max(0, s.box[0] * 100 - pad);
      const h = Math.min(100 - top, (s.box[2] - s.box[0]) * 100 + pad * 2);
      band = `--hl-top:${top}%;--hl-h:${h}%`;     // geometry only — appearance lives in .cb-stmt-hl
    }
    // a statement opened at a page shows its nav until it is known to have one page only
    const navOn = s.status === 'ready' && (s.pageOnly ? s.pageCount !== 1 : s.pageCount > 1);
    const atEnd = s.pageCount ? s.page >= s.pageCount - 1 : false;
    const nav = navOn ? html`<span class="cb-stmt-nav">
        <button class="cb-stmt-pg" ?disabled=${s.page <= 0} title=${t('cashbook.prev_page', 'Previous page')} @click=${() => gotoPage(s.page - 1)}>‹</button>
        <span class="cb-stmt-page mono">p${(s.page || 0) + 1}${s.pageCount ? '/' + s.pageCount : ''}</span>
        <button class="cb-stmt-pg" ?disabled=${atEnd} title=${t('cashbook.next_page', 'Next page')} @click=${() => gotoPage(s.page + 1)}>${icon('chevron-right', {size: 14})}</button>
      </span>` : [];
    return html`<div class="cb-stmt-overlay" @click=${e => { if(e.target === e.currentTarget) close(); }}>
      <div class="cb-stmt-modal" role="dialog" aria-label="Statement source">
        <div class="cb-stmt-head">
          <span class="fw-700 cb-stmt-title">${s.title}</span>
          ${nav}
          <span class="cb-mode-spacer"></span>
          <button class="cb-clear-btn" title=${t('common.close', 'Close')} @click=${close}>✕</button>
        </div>
        <div class="cb-stmt-body">
          ${s.status === 'loading'
            ? html`<div class="cb-stmt-locating">${s.pageOnly
                ? t('cashbook.loading_page', 'Loading the statement page…')
                : t('cashbook.locating', 'Asking Sleek to find this line on the statement…')}</div>`
            : s.status === 'error'
              ? html`<div class="muted-block cb-stmt-err">${s.error}</div>`
              : html`<div class="cb-stmt-img-wrap">
                  <img class="cb-stmt-img" src=${s.imgUrl} alt="Statement page">
                  ${s.box ? html`<div class="cb-stmt-hl" style=${band}></div>` : []}
                </div>
                ${!s.box && !s.pageOnly ? html`<div class="cb-stmt-noline">${s.pageCount > 1
                    ? t('cashbook.locate_noline_multi', 'Showing the statement — the exact line couldn’t be pinpointed. Use ‹ › to look through the pages.')
                    : t('cashbook.locate_noline', 'Showing the statement — the exact line could not be pinpointed automatically.')}</div>` : []}`}
        </div>
      </div>
    </div>`;
  }

  return { open, openPage, close, state, view: () => (state.value ? modal(state.value) : []) };
}
